import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Download, Printer } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useShiftTypes } from '../../hooks/useSettings';
import { isoDate } from '../../utils/time';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = d => { try { return format(parseISO(d), 'd MMM'); } catch { return d; } };
const fmtVal = v => parseFloat(v || 0) === 0 ? '0' : parseFloat(v).toFixed(parseFloat(v) % 1 === 0 ? 0 : 1);

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-3 py-2.5 text-xs">
      <p className="font-semibold text-slate-700 mb-1.5">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center gap-1.5 mb-0.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }}/>
          <span className="text-slate-600 capitalize">{p.dataKey}:</span>
          <span className="font-semibold text-slate-800">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function ActivityReport() {
  const today     = new Date();
  const [view, setView]         = useState('staff');
  const [metric, setMetric]     = useState('hours');
  const [shiftType, setShiftType] = useState('');
  const [startDate, setStart]   = useState(format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(today, 'yyyy-MM-dd'));
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const printRef = useRef();
  const shiftTypeOptions = useShiftTypes();

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate, view, metric });
    if (shiftType) p.set('shift_type', shiftType);
    api.get(`/reports/activity?${p}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load activity report'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, view, metric, shiftType]);

  useEffect(() => { load(); }, [load]);

  function applyPreset(preset) {
    const now = new Date(); const y = now.getFullYear(); const m = now.getMonth();
    const iso = isoDate;
    if (preset === 'this-month')        { setStart(iso(new Date(y, m, 1)));     setEnd(iso(now)); }
    else if (preset === 'last-month')   { setStart(iso(new Date(y, m-1, 1))); setEnd(iso(new Date(y, m, 0))); }
    else if (preset === 'this-quarter') { const q = Math.floor(m/3)*3; setStart(iso(new Date(y, q, 1))); setEnd(iso(now)); }
    else if (preset === 'this-year')    { setStart(iso(new Date(y, 0, 1))); setEnd(iso(now)); }
    else if (preset === 'this-fy')      { const fyY = m >= 6 ? y : y-1; setStart(iso(new Date(fyY, 6, 1))); setEnd(iso(now)); }
  }

  // ── Download CSV ──────────────────────────────────────────────────────────
  function downloadCSV() {
    if (!data?.table?.length) return;
    const cols = ['Name', 'Booked', 'Pending', 'Cancelled', 'Absent', 'Total'];
    const rows = data.table.map(r => [r.name, r.booked, r.pending, r.cancelled, r.absent, r.total]);
    const csv  = [cols, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a    = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `activity_${view}_${startDate}_${endDate}.csv`;
    a.click();
  }

  function printReport() { window.print(); }

  const summary = data?.summary || {};
  const label   = data?.shift_type_label;

  const byDay = (data?.by_day || []).map(d => ({
    date:      fmt(d.date),
    booked:    parseFloat(d.booked    || 0),
    pending:   parseFloat(d.pending   || 0),
    cancelled: parseFloat(d.cancelled || 0),
  }));

  const total = parseFloat(summary.total || 0);
  const pieData = [
    { name: 'Booked',    value: parseFloat(summary.booked    || 0), color: '#22c55e' },
    { name: 'Pending',   value: parseFloat(summary.pending   || 0), color: '#ef4444' },
    { name: 'Cancelled', value: parseFloat(summary.cancelled || 0), color: '#f59e0b' },
    { name: 'Absent',    value: parseFloat(summary.absent    || 0), color: '#64748b' },
  ].filter(d => d.value > 0);

  const metricLabel = metric === 'mileage' ? 'Mileage' : metric === 'expenses' ? 'Expenses' : 'Hours';

  return (
    <div className="p-6 max-w-full" ref={printRef}>
      {/* Page header */}
      <div className="flex items-baseline gap-2 mb-6">
        <h1 className="text-xl font-bold text-slate-800">Report</h1>
        <span className="text-slate-400 text-base">Activity</span>
      </div>

      {/* Controls bar */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-5">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <div className="flex items-center gap-6">
            {/* Staff / Client radio */}
            {['staff', 'client'].map(v => (
              <label key={v} className="flex items-center gap-2 cursor-pointer select-none">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors
                  ${view === v ? 'border-red-500' : 'border-slate-300'}`}>
                  {view === v && <div className="w-2 h-2 rounded-full bg-red-500"/>}
                </div>
                <input type="radio" className="hidden" value={v} checked={view === v} onChange={() => setView(v)}/>
                <span className={`text-sm font-medium ${view === v ? 'text-slate-800' : 'text-slate-500'}`}>
                  {v === 'staff' ? 'Staff' : 'Client'}
                </span>
              </label>
            ))}

            {/* Metric dropdown */}
            <select value={metric} onChange={e => setMetric(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="hours">Hours</option>
              <option value="mileage">Mileage</option>
              <option value="expenses">Expenses</option>
            </select>

            {/* Shift type filter */}
            <select value={shiftType} onChange={e => setShiftType(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">All</option>
              {shiftTypeOptions.map(t => <option key={t.id || t.name} value={t.name}>{t.name}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            {[['this-month','This Month'],['last-month','Last Month'],['this-quarter','Quarter'],['this-year','This Year'],['this-fy','This FY']].map(([key, lbl]) => (
              <button key={key} onClick={() => applyPreset(key)}
                className="px-2.5 py-1 text-xs font-medium rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 transition-colors">
                {lbl}
              </button>
            ))}
            <div className="flex items-center gap-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white">
              <input type="date" value={startDate} onChange={e => setStart(e.target.value)}
                className="focus:outline-none bg-transparent text-slate-700 text-sm"/>
              <span className="text-slate-400 mx-1">–</span>
              <input type="date" value={endDate} onChange={e => setEnd(e.target.value)}
                className="focus:outline-none bg-transparent text-slate-700 text-sm"/>
            </div>
          </div>
        </div>

        {/* Chart + Pie section */}
        <div className="p-5">
          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"/>
            </div>
          ) : byDay.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
              No activity data for the selected range
            </div>
          ) : (
            <div className="flex gap-6">
              {/* Area chart */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-4 mb-3">
                  <p className="text-sm font-semibold text-slate-700">
                    Activity{label ? ` for ${label}` : ''} — {metricLabel}
                  </p>
                  {summary.distinct_staff > 0 && (
                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{summary.distinct_staff} staff</span>
                  )}
                  {summary.distinct_clients > 0 && (
                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{summary.distinct_clients} clients</span>
                  )}
                </div>
                <div className="flex items-center gap-5 mb-3">
                  {[['Booked','#22c55e'], ['Pending','#ef4444'], ['Cancelled','#f59e0b']].map(([l, c]) => (
                    <div key={l} className="flex items-center gap-1.5 text-xs text-slate-600">
                      <span className="w-8 h-0.5 inline-block rounded" style={{ background: c }}/>
                      {l}
                    </div>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={byDay} margin={{ top: 5, right: 10, left: -25, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gBooked"    x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/><stop offset="95%" stopColor="#22c55e" stopOpacity={0.05}/></linearGradient>
                      <linearGradient id="gPending"   x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ef4444" stopOpacity={0.25}/><stop offset="95%" stopColor="#ef4444" stopOpacity={0.02}/></linearGradient>
                      <linearGradient id="gCancelled" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25}/><stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02}/></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Area type="monotone" dataKey="booked"    stroke="#22c55e" fill="url(#gBooked)"    strokeWidth={2} dot={{ r: 3, fill: '#22c55e' }} activeDot={{ r: 4 }}/>
                    <Area type="monotone" dataKey="pending"   stroke="#ef4444" fill="url(#gPending)"   strokeWidth={2} dot={{ r: 3, fill: '#ef4444' }} activeDot={{ r: 4 }}/>
                    <Area type="monotone" dataKey="cancelled" stroke="#f59e0b" fill="url(#gCancelled)" strokeWidth={2} dot={{ r: 3, fill: '#f59e0b' }} activeDot={{ r: 4 }}/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Pie chart */}
              {pieData.length > 0 && (
                <div className="w-52 shrink-0 flex flex-col items-center justify-center">
                  <p className="text-sm font-bold text-slate-800 mb-1">Total {total}</p>
                  <ResponsiveContainer width={180} height={180}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" cx="50%" cy="50%"
                        outerRadius={72} innerRadius={30} paddingAngle={2} startAngle={90} endAngle={-270}>
                        {pieData.map((d, i) => <Cell key={i} fill={d.color}/>)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [`${v} (${total > 0 ? ((v/total)*100).toFixed(1) : 0}%)`, n]}/>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1 mt-1">
                    {pieData.map(d => (
                      <div key={d.name} className="flex items-center gap-2 text-xs text-slate-600">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }}/>
                        <span>{d.name}</span>
                        <span className="font-semibold text-slate-700 ml-auto pl-2">{d.value}</span>
                      </div>
                    ))}
                  </div>
                  {pieData.length > 0 && total > 0 && (
                    <p className="text-2xl font-bold text-slate-700 mt-2">
                      {((parseFloat(summary.booked||0)/total)*100).toFixed(1)}%
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="border-t border-slate-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/40">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 w-64"/>
                <th className="px-5 py-3 text-xs font-semibold text-slate-600 text-center">Booked</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-600 text-center">Pending</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-600 text-center">Cancelled</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-600 text-center">Absent</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-600 text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400 text-sm">Loading…</td></tr>
              ) : !data?.table?.length ? (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400 text-sm">No data for selected range</td></tr>
              ) : (
                data.table.map((row, i) => (
                  <tr key={row.id || i} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-5 py-3 text-slate-700 font-medium">{row.name}</td>
                    <td className="px-5 py-3 text-center text-slate-600">{fmtVal(row.booked)}</td>
                    <td className="px-5 py-3 text-center text-slate-600">{fmtVal(row.pending)}</td>
                    <td className="px-5 py-3 text-center text-slate-600">{fmtVal(row.cancelled)}</td>
                    <td className="px-5 py-3 text-center text-slate-600">{fmtVal(row.absent)}</td>
                    <td className="px-5 py-3 text-center font-semibold text-slate-800">{fmtVal(row.total)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer with download / print */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <button onClick={downloadCSV}
            className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
            title="Download CSV">
            <Download size={16}/>
          </button>
          <button onClick={printReport}
            className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
            title="Print">
            <Printer size={16}/>
          </button>
        </div>
      </div>
    </div>
  );
}
